import React, {useEffect, useState} from "react";
import InputField from "../common/inputs/InputField.tsx";
import {useForm} from "react-hook-form";
import {Box, Grid, Typography} from "@mui/material";
import {useSelector} from "react-redux";
import {z} from "zod";
import {axiosFunction, toastMessage} from "../commonFunctions";
import {v2ReactURL} from "../../utils/constant.ts";
// import {ErrorState} from "../../pages/Contact/Contact.tsx";
import {apiResponseHandler} from "../../utils/functions/apiResponseHandler.ts";
import {customNumberInputHandler} from "../../utils/functions/customNumberInputHandler.ts";
import { ErrorState } from "../../pages/Contact/MainDataTable.tsx";
import {ErrorMessage} from "../../utils/ErrorMessage.tsx";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

const PointSetupSchema = z.object({
    earnAmount: z.number().nullable(),
    earnPoint: z.number().nullable(),
});

export type PointSetupType = z.infer<typeof PointSetupSchema>;

interface PointSetupData {
    loyaltyPointsSetupId: number | 0,
    earnAmount: number | null,
    earnPoint: number | null,
}

interface Permissions {
    isView: number;
    isEdit?: number;
    isDelete?: number;
    isInsert?: number;
}

interface CampaignTabProps {
    permissions: Permissions;
}

const pointSetupSchema = yup.object().shape({
    earnAmount: yup
        .string()
        .required("Earn Amount is required")
        .test("is-number", "Earn Amount must be a valid number", (value) => !isNaN(Number(value)))
        .test("greater-than-zero", "Earn Amount must be greater than 0", (value) => Number(value) >= 0),
    earnPoint: yup
        .string()
        .required("Earn Point is required")
        .test("is-number", "Earn Point must be a valid number", (value) => !isNaN(Number(value)))
        .test("greater-than-zero", "Earn Point must be greater than 0", (value) => Number(value) >= 0),
});

const PointSetupTab: React.FC<CampaignTabProps> = ({permissions})  => {
    const sessionScope = useSelector((state: any) => state.sessionData?.sessionScope);
    const decimalPoint = sessionScope?.decimalPoint || 2;
    const [pointSetup, setPointSetup] = useState<PointSetupData | null>(null);
    const [isLoadingBtn, setIsLoadingBtn] = useState(false);

    const {
        register,
        handleSubmit,
        setValue,
        formState: { errors },
    } = useForm<PointSetupType>({
        defaultValues: {
            earnAmount: null,
            earnPoint: null
        },
        resolver: yupResolver(pointSetupSchema as any),
        mode: "onChange",
    });

    const onSubmit = async (data: PointSetupType) => {
        setIsLoadingBtn(true);
        const response = await axiosFunction(
            `${v2ReactURL}/loyalty/point/setup`,
            "POST",
            {
                loyaltyPointsSetupId: pointSetup?.loyaltyPointsSetupId || 0,
                earnPoint: data.earnPoint,
                earnAmount: data.earnAmount,
            }
        );

        const error: ErrorState | null = apiResponseHandler(response, true);
        if (error != null) {
            toastMessage(error.message);
            setIsLoadingBtn(false);
            return;
        }

        if (response.data.status) {
            toastMessage("Points setup successful", "success");
        }
        setIsLoadingBtn(false);
    };

    useEffect(() => {
        const fetchPointsSetupData = async () => {
            const response = await axiosFunction(
                `${v2ReactURL}/loyalty/point/setup`,
                "GET",
            );
            const error: ErrorState | null = apiResponseHandler(response, true);
            if (error != null) {
                toastMessage(error.message);
                return;
            }
            if (response.data.response) {
                setPointSetup(response.data.response)
            }
        }
        fetchPointsSetupData();
    }, []);

    useEffect(() => {
        setValue("earnPoint", pointSetup?.earnPoint ?? null);
        setValue("earnAmount", pointSetup?.earnAmount ?? null);
    }, [pointSetup]);

    return (
        <Box sx={{maxWidth: 600, p: 3}}>
            <Typography variant="h6" gutterBottom>Earn Points</Typography>
            <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={5}>
                    <InputField
                        labelName="Amount"
                        placeholder="Enter amount"
                        name="earnAmount"
                        id="earnAmount"
                        register={register}
                        required
                        inputType="text"
                        onKeyDown={customNumberInputHandler(5, decimalPoint)}
                        disabled={!Boolean(permissions.isEdit)}
                        errorMessage={ErrorMessage(errors?.earnAmount?.message)}
                    />
                </Grid>
                <Grid item xs={12} sm={2} sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                    <Typography variant="h6">=</Typography>
                </Grid>
                <Grid item xs={12} sm={5}>
                    <InputField
                        labelName="Point"
                        placeholder="Enter points"
                        name="earnPoint"
                        id="earnPoint"
                        register={register}
                        required={true}
                        inputType="text"
                        onKeyDown={customNumberInputHandler(5, decimalPoint)}
                        disabled={!Boolean(permissions.isEdit)}
                        errorMessage={ErrorMessage(errors?.earnPoint?.message)}
                    />
                </Grid>
            </Grid>

            {Boolean(permissions.isEdit) && (
                <Box mt={3} display="flex" justifyContent="flex-start">
                    <button
                        disabled={isLoadingBtn}
                        onClick={handleSubmit(onSubmit)}
                        className="btn btn-sm btn-primary"
                        style={{ position: "relative", display: "inline-block" }}
                    >
                        Submit
                    </button>
                </Box>
            )}
        </Box>
    );
};

export default PointSetupTab;
